import React, {useEffect, useRef, useState} from "react";
import {AlertCircle, Eye, EyeOff, Loader2} from "lucide-react";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import {Button} from "../../components/nuclear/button";
import {Input} from "../../components/nuclear/input";
import {Label} from "../../components/nuclear/label";
import "../../components/nuclear/ui.css";
import "./SignInPage.module.css";
import "./GoogleOAuthCallbackPage.module.css";
import {signInUser} from "./authServices";
// Google sign-in disabled — see docs/deployment/google-integration-shelved.md
// import {getGoogleAuthFailureMessage, signInWithGoogle} from "./authServices";
import {completeAuthentication, useAuthenticationPage} from "./authPageHelpers";

export default function SignInPage({redirection}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);

    useAuthenticationPage(dispatch, redirection);

    useEffect(() => {
        document.title = "Log In - Animotive VR Dashboard";
        return () => {
            document.title = "Animotive VR Dashboard";
        };
    }, []);

    const isValidEmail = (value) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    };

    async function handleSubmit(event) {
        event.preventDefault();
        setErrorMessage(null);
        const trimmedEmail = email.trim();
        const password = passwordInputRef.current?.value ?? "";

        if (!trimmedEmail) {
            setErrorMessage("Email is required");
            emailInputRef.current?.focus();
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setErrorMessage("Please enter a valid email address (e.g., user@example.com)");
            emailInputRef.current?.focus();
            return;
        }

        if (!password) {
            setErrorMessage("Password is required");
            passwordInputRef.current?.focus();
            return;
        }

        if (password.length < 8) {
            setErrorMessage("Password must be at least 8 characters long");
            passwordInputRef.current?.focus();
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await signInUser({email: trimmedEmail, password});

            if (response.nextStep?.signInStep && response.nextStep.signInStep !== "DONE") {
                setErrorMessage("Additional sign-in verification is required for this account.");
                return;
            }

            await completeAuthentication(dispatch, navigate);
        } catch (error) {
            console.error("Login failed:", error);
            setErrorMessage("Invalid email or password. Please try again.");
            passwordInputRef.current?.focus();
        } finally {
            setIsSubmitting(false);
        }
    }

    // Google sign-in disabled — see docs/deployment/google-integration-shelved.md
    // async function handleGoogleSignIn() {
    //     setErrorMessage(null);
    //
    //     try {
    //         await signInWithGoogle("signin");
    //     } catch (error) {
    //         setErrorMessage(getGoogleAuthFailureMessage(error));
    //     }
    // }

    return (
        <div className={"auth-page-shell"}>
            <div className={"auth-page-container"}>
                <img
                    src={"/img/Branding/AnimotiveLogoHeader.png"}
                    alt={"Animotive"}
                    className={"auth-logo"}
                />

                <div className={"auth-tabs"} role={"tablist"}>
                    <button
                        type={"button"}
                        role={"tab"}
                        aria-selected={"true"}
                        className={"auth-tab-button"}
                    >
                        Log In
                    </button>
                    <RouterLink
                        to={"/register"}
                        role={"tab"}
                        aria-selected={"false"}
                        className={"auth-tab-link"}
                    >
                        Create Account
                    </RouterLink>
                </div>

                {errorMessage && (
                    <div
                        id={"login-error"}
                        role={"alert"}
                        aria-live={"assertive"}
                        aria-atomic={"true"}
                        className={"auth-error-alert"}
                    >
                        <AlertCircle size={18} className={"auth-alert-icon"} />
                        <span>{errorMessage}</span>
                    </div>
                )}

                {isSubmitting && (
                    <div
                        role={"status"}
                        aria-live={"polite"}
                        className={"sr-only"}
                    >
                        Signing in, please wait...
                    </div>
                )}

                <form onSubmit={handleSubmit} className={"auth-form-grid"} noValidate={true}>
                    <div>
                        <Label htmlFor={"login-email"} className={"auth-field-label"}>
                            Email
                            <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                        </Label>
                        <Input
                            ref={emailInputRef}
                            id={"login-email"}
                            name={"email"}
                            type={"email"}
                            placeholder={"Enter your email"}
                            required={true}
                            autoComplete={"email"}
                            value={email}
                            onChange={(event) => {
                                setEmail(event.target.value);
                                setErrorMessage(null);
                            }}
                            aria-required={"true"}
                            aria-invalid={errorMessage && !email ? "true" : "false"}
                            aria-describedby={errorMessage ? "login-error" : undefined}
                        />
                    </div>

                    <div>
                        <Label htmlFor={"login-password"} className={"auth-field-label"}>
                            Password
                            <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                        </Label>
                        <div className={"auth-password-wrap"}>
                            <Input
                                ref={passwordInputRef}
                                id={"login-password"}
                                name={"password"}
                                type={showPassword ? "text" : "password"}
                                placeholder={"Enter your password"}
                                required={true}
                                autoComplete={"current-password"}
                                onChange={() => setErrorMessage(null)}
                                aria-required={"true"}
                                aria-invalid={errorMessage && !(passwordInputRef.current?.value ?? "") ? "true" : "false"}
                                aria-describedby={errorMessage ? "login-error" : undefined}
                                className={"auth-input-with-icon"}
                            />
                            <button
                                type={"button"}
                                onClick={() => setShowPassword((current) => !current)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                aria-pressed={showPassword ? "true" : "false"}
                                aria-controls={"login-password"}
                                className={"auth-password-toggle"}
                            >
                                {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                            </button>
                        </div>
                    </div>

                    <div className={"auth-link-row"}>
                        <RouterLink to={"/forgot-password"} className={"auth-link"}>
                            Forgot your password?
                        </RouterLink>
                    </div>

                    <Button
                        type={"submit"}
                        disabled={isSubmitting}
                        aria-busy={isSubmitting ? "true" : "false"}
                        fullWidth={true}
                    >
                        {isSubmitting && <Loader2 size={18}/>}
                        {isSubmitting ? "Signing in..." : "Log In"}
                    </Button>
                </form>

                {/* Google sign-in disabled — see docs/deployment/google-integration-shelved.md
                <div className={"auth-google-divider"}>or</div>

                <button
                    type={"button"}
                    className={"auth-google-btn"}
                    onClick={() => {
                        void handleGoogleSignIn();
                    }}
                    aria-label={"Sign in with Google"}
                >
                    <svg className={"auth-google-icon"} width={"18"} height={"18"} viewBox={"0 0 24 24"} aria-hidden={"true"}>
                        <path fill={"#4285F4"} d={"M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"}/>
                        <path fill={"#34A853"} d={"M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"}/>
                        <path fill={"#FBBC05"} d={"M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"}/>
                        <path fill={"#EA4335"} d={"M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"}/>
                    </svg>
                    Sign in with Google
                </button>
                */}
            </div>
        </div>
    );
}
